/**
 * Portal role → capability map. One source of truth for the server (route
 * gates) and the client (nav / action visibility). Permissions are trivial to
 * retune here; the server remains the real enforcer.
 */
export const PORTAL_ROLES = ["admin", "manager", "sales"] as const;
export type PortalRole = (typeof PORTAL_ROLES)[number];

export const PORTAL_CAPABILITIES = [
  "view:all",
  "submissions:remind",
  "links:create",
  "links:revoke",
  "users:manage",
  "org:view",
  "org:manage",
  "practices:view",
  "practices:create",
  "practices:manage",
  "products:view",
  "products:manage",
  "enrollments:create",
  "enrollments:manage",
  "attribution:view",
  "attribution:manage",
  "assignments:manage",
  "disputes:create",
  "integrations:manage",
  "commissions:manage",
  "commissions:approve",
  "commissions:publish",
  "payments:manage",
  "crm:work",
  "crm:configure",
  "documents:read",
  "documents:upload",
  "documents:review",
  "documents:request",
  "communications:read",
  "communications:compose",
  "communications:manage",
  "exceptions:read",
  "exceptions:work",
  "exceptions:configure",
  "reps:read",
  "contacts:edit-own",
] as const;
export type PortalCapability = (typeof PORTAL_CAPABILITIES)[number];

export const ROLE_LABELS: Record<PortalRole, string> = {
  admin: "Administrator",
  manager: "Operations",
  sales: "Team member",
};

const ROLE_CAPABILITIES: Record<PortalRole, readonly PortalCapability[]> = {
  admin: PORTAL_CAPABILITIES,
  manager: [
    "view:all",
    "submissions:remind",
    "links:create",
    "links:revoke",
    "org:view",
    "practices:view",
    "practices:create",
    "practices:manage",
    "products:view",
    "enrollments:create",
    "enrollments:manage",
    "attribution:view",
    "assignments:manage",
    "disputes:create",
    "crm:work",
    "documents:read",
    "documents:upload",
    "documents:review",
    "documents:request",
    "communications:read",
    "communications:compose",
    "communications:manage",
    "exceptions:read",
    "exceptions:work",
    "reps:read",
  ],
  sales: [
    "links:create", "org:view", "practices:view", "practices:create", "products:view",
    "enrollments:create", "attribution:view", "disputes:create", "crm:work",
    "documents:read", "documents:upload", "communications:read", "communications:compose",
    "exceptions:read", "exceptions:work",
    "reps:read",
    "contacts:edit-own",
  ],
};

export function isPortalRole(value: string): value is PortalRole {
  return (PORTAL_ROLES as readonly string[]).includes(value);
}

export function capabilitiesFor(role: PortalRole): PortalCapability[] {
  return [...ROLE_CAPABILITIES[role]];
}

export function hasCapability(role: PortalRole, cap: PortalCapability): boolean {
  return ROLE_CAPABILITIES[role].includes(cap);
}

export function roleLabel(role: PortalRole): string {
  return ROLE_LABELS[role];
}
